// ============================================================================
// Reading Ask AI's reply as it is written (item #944, batch 2: streaming).
//
// The app asks am-copilot for a stream; the function answers with server-sent
// events: `text` (the next words), `step` (a round of lookups started), then
// `done` with the same body the one-shot reply always had — or `error`. The
// words shown while it streams are a preview; `done` is what is kept.
// supabase/functions/am-copilot/shape.ts writes these events; ask-ai-stream.test.ts
// feeds this reader the function's own output so the two cannot drift.
// ============================================================================

import type { CopilotInvokeResponse } from '../../shared/types';

export interface StreamEvent {
  event: string;
  data: unknown;
}

/** Split a buffer into complete events; the incomplete tail comes back as `rest`. */
export function splitEvents(buffer: string): { events: StreamEvent[]; rest: string } {
  const events: StreamEvent[] = [];
  const parts = buffer.split('\n\n');
  const rest = parts.pop() ?? '';
  for (const p of parts) {
    let event = 'message';
    const data: string[] = [];
    for (const line of p.split('\n')) {
      if (line.startsWith('event:')) event = line.slice(6).trim();
      else if (line.startsWith('data:')) data.push(line.slice(5).trimStart());
    }
    if (data.length === 0) continue;
    events.push({ event, data: JSON.parse(data.join('\n')) });
  }
  return { events, rest };
}

/**
 * Read a turn's stream to its end. `onText` gets the words so far each time
 * more arrive; `onActivity` is called on every event (the caller's "still
 * alive" timer). Resolves with the final body; throws on an `error` event or a
 * stream that ends before `done`.
 */
export async function readTurnStream(
  body: ReadableStream<Uint8Array>,
  onText: (soFar: string) => void,
  onActivity: () => void,
): Promise<CopilotInvokeResponse> {
  const reader = body.getReader();
  const decoder = new TextDecoder();
  let buffer = '';
  let text = '';
  for (;;) {
    const { value, done } = await reader.read();
    if (done) break;
    buffer += decoder.decode(value, { stream: true });
    const { events, rest } = splitEvents(buffer);
    buffer = rest;
    for (const e of events) {
      onActivity();
      if (e.event === 'text') {
        const d = (e.data as { delta?: unknown })?.delta;
        if (typeof d === 'string') {
          text += d;
          onText(text);
        }
      } else if (e.event === 'done') {
        return e.data as CopilotInvokeResponse;
      } else if (e.event === 'error') {
        const words = (e.data as { reply_text?: unknown })?.reply_text;
        throw new Error(typeof words === 'string' && words ? words : 'Ask AI hit a problem it could not recover from.');
      }
    }
  }
  throw new Error('Ask AI stopped before it finished answering.');
}
